import "./TableModal.scss";
import {useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";
import ModalTemplate from "../../ModalTemplate/ModalTemplate";
import Button from "../../Button/Button";
import Input from "../../Input/Input";
import useVariables from "../../../contexts/variablesContext/useVariables";
import {findObjectArrayPaths, getLeafPaths, getNestedValue} from "../../../utils/Utils";
import SuggestionInput from "../../suggestionInput/SuggestionInput";
import Icon, {trashIcon} from "../../Icon/Icon";

const TableModal = ({editor, onClose, initialData}) => {
    const {variables} = useVariables();
    const [customRows, setCustomRows] = useState([]);
    const [selectedList, setSelectedList] = useState("")

    const arrays = findObjectArrayPaths(variables)

    const handleAddRow = () => setCustomRows(prev => [...prev, {id: Math.max(-1, ...prev.map(r => r.id)) + 1, name: "", value: ""}])


    const handleDeleteRow = (id) => setCustomRows(prev => prev.filter(row => row.id !== id));


    const handleSave = () => {
        if (!selectedList || !customRows.length) return;

        const {state, view} = editor;
        const {schema} = state;

        const columns = customRows
            .filter(row => row.name)
            .map(({name, value}) => ({name, valuePath: value}));

        if (initialData) {
            const {tr} = state;

            state.doc.descendants((node, pos) => {
                if (node.type.name === "variableTable" && node.attrs.id === initialData.id) {
                    tr.setNodeMarkup(pos, null, {
                        ...node.attrs,
                        sourcePath: selectedList,
                        columns
                    })
                }
            })

            view.dispatch(tr);
        } else {
            const node = schema.nodes.variableTable.create({
                id: uuidv4(),
                sourcePath: selectedList,
                columns
            })

            const {from} = state.selection;
            view.dispatch(state.tr.insert(from, node));
        }

        onClose();
    }

    const handleSelectList = (e) => {
        const path = e.target.value;
        setSelectedList(path);

        if (!path) {
            setCustomRows([{id: 0, name: "", value: ""}]);
            return;
        }

        const list = getNestedValue(variables, path);

        if (!list?.length) return;

        const keys = Object.keys(list[0]).filter(key => {
            const value = list[0][key];
            return !(Array.isArray(value) && value.length > 0 && typeof value[0] === "object");
        });

        const rows = keys.map((key, index) => {
            const value = list[0][key];

            if (value && typeof value === "object" && !Array.isArray(value)) {
                const subPaths = getLeafPaths(`${path}.${key}`, value).map(p => `@${p}`).join(" ");

                return {id: index, name: key, value: subPaths};
            }

            if (Array.isArray(value)) {
                return {id: index, name: key, value: `@${path}.inline.${key}`};
            }

            return {id: index, name: key, value: `@${path}.${key}`};
        })

        setCustomRows(rows);
    }

    useEffect(() => {
        if(!initialData) return;

        setSelectedList(initialData.sourcePath);
        setCustomRows(
            initialData.columns.map((col, index) => ({
                id: index,
                name: col.name,
                value: col.valuePath
            }))
        );

    }, [initialData])


    return (
        <ModalTemplate
            onClose={onClose}
            title="Create table"
            footerActions={
                <>
                    <Button onClick={handleSave}>Save</Button>
                    <Button variant='secondary' onClick={onClose}>Cancel</Button>
                </>
            }
        >
            <select value={selectedList} onChange={handleSelectList}>
                <option value="">Select list</option>
                {arrays.map(({label, path}) => (
                    <option key={path} value={path}>
                        {label}
                    </option>
                ))}
            </select>

            {selectedList !== "" &&
                <>
                    {!!customRows.length && <div className="rows">
                        <div className="custom-row header">
                            <div>Column Name:</div>
                            <div>Value:</div>
                        </div>

                        {customRows.map((row) =>
                            <div key={row.id} className="custom-row">
                                <Input
                                    value={row.name}
                                    onChange={e =>
                                        setCustomRows(prev => prev.map(r => r.id === row.id ? {
                                        ...r,
                                        name: e.target.value
                                    } : r))}
                                />
                                <SuggestionInput
                                    value={row.value}
                                    variables={variables}
                                    onChange={e => setCustomRows(prev => prev.map(r => r.id === row.id ? {
                                        ...r,
                                        value: e.target.value
                                    } : r))}
                                />
                                <Button onClick={() => handleDeleteRow(row.id)}>
                                    <Icon svg={trashIcon}/>
                                </Button>
                            </div>
                        )}
                    </div>}

                    <Button onClick={handleAddRow}>+ Add Row</Button>
                </>
            }
        </ModalTemplate>
    )
}

export default TableModal